import { useState, useRef, useEffect, useCallback } from "react";
import "../styles/Chatbot.css";
import axiosClient from "../api/axiosClient";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function useTTS() {

  const utterRef = useRef(null);
  const [speaking, setSpeaking] = useState(null); // msg id
  const speak = useCallback((id, text) => {

    window.speechSynthesis.cancel();

    if (speaking === id) { setSpeaking(null); return; }

    const utter = new SpeechSynthesisUtterance(text);

    utter.onend = () => setSpeaking(null);
    utter.onerror = () => setSpeaking(null);
    utterRef.current = utter;
    setSpeaking(id);
    window.speechSynthesis.speak(utter);
  }, [speaking]);


  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(null);
  }, []);
  return { speaking, speak, stop };
}

// ─── SESSION ID ─────────────────────────────────────────────
const SESSION_KEY = "guestSessionId";

function getGuestSessionId() {
  let id = localStorage.getItem(SESSION_KEY);

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }

  return id;
}


// ─── API CALL ─────────────────────────────────────────────────────────────────
// ─── API CALL ───────────────────────────────────────────────
async function sendMessageToAPI(text, conversationHistory) {
 
  const token = localStorage.getItem("token");
  const guestSessionId = getGuestSessionId();

  const payload = {
    question: text,
    ...(token
      ? {}
      : { guestSessionId }),
  };

  // console payload
  console.log("Payload:", payload);

  const { data } = await axiosClient.post(
    "/no-auth/api/users/aiAssistantChat",
    payload,
     {
    headers: {
      Authorization: ` ${token}`,
    },
  }
  );

  return data;
}

// ─── PRE-CHAT FORM ────────────────────────────────────────────────────────────
function PreChatForm({ botName, onSubmit }) {
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!email.trim()) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      e.email = "Enter a valid email address.";
    }
    if (!phone.trim()) {
      e.phone = "Phone number is required.";
    } else if (!/^\+?[\d\s\-()]{7,15}$/.test(phone.trim())) {
      e.phone = "Enter a valid phone number.";
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      await axiosClient.post("no-auth/api/users/saveContact", {
        email: email.trim(),
        phone: phone.trim(),
      });
      onSubmit({ email: email.trim(), phone: phone.trim() });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to save details. Please try again.";
      setErrors({ phone: msg }); // show error below the last field
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (ev) => {
    if (ev.key === "Enter") handleSubmit();
  };

  return (
    <div className="wapp__prechat">
  <br/>
  <br/>

      <div className="wapp__prechat-fields">
        <div className={`wapp__prechat-field${errors.email ? " wapp__prechat-field--error" : ""}`}>
          <label className="wapp__prechat-label" htmlFor="pc-email">
            <span className="wapp__prechat-label-icon">✉️</span> Email address
          </label>
          <input
            id="pc-email"
            type="email"
            className="wapp__prechat-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
            onKeyDown={handleKeyDown}
            autoComplete="email"
          />
          {errors.email && <span className="wapp__prechat-error">{errors.email}</span>}
        </div>

        <div className={`wapp__prechat-field${errors.phone ? " wapp__prechat-field--error" : ""}`}>
          <label className="wapp__prechat-label" htmlFor="pc-phone">
            <span className="wapp__prechat-label-icon">📱</span> Phone number
          </label>
          <input
            id="pc-phone"
            type="tel"
            className="wapp__prechat-input"
            placeholder="+91 98765 43210"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: "" })); }}
            onKeyDown={handleKeyDown}
            autoComplete="tel"
          />
          {errors.phone && <span className="wapp__prechat-error">{errors.phone}</span>}
        </div>

        <button
          className="wapp__prechat-submit"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <span className="wapp__prechat-spinner" />
          ) : (
            <>Start Chat <span className="wapp__prechat-arrow">→</span></>
          )}
        </button>

        <p className="wapp__prechat-privacy">
          🔒 Your info is private and never shared.
        </p>
      </div>
    </div>
  );
}

// ─── WAPP BOT AVATAR ──────────────────────────────────────────────────────────
function WappBotAvatar({ invisible = false }) {
  return (
    <div
      className={`wapp__avatar wapp__avatar--bot${invisible ? " wapp__avatar--invisible" : ""}`}
      aria-hidden="true"
    >
      🤖
    </div>
  );
}

function WappUserAvatar({ src, invisible = false }) {
  return (
    <div
      className={`wapp__avatar wapp__avatar--user${invisible ? " wapp__avatar--invisible" : ""}`}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="You" /> : <span>👤</span>}
    </div>
  );
}

// ─── WAPP MESSAGE ─────────────────────────────────────────────────────────────
function WappMessage({ msg, showAvatar, userAvatarSrc }) {
  const isBot = msg.role === "bot";
  return (
    <div className={`wapp__message-row wapp__message-row--${isBot ? "bot" : "user"}`}>
      {isBot ? <WappBotAvatar invisible={!showAvatar} /> : <WappUserAvatar src={userAvatarSrc} invisible={!showAvatar} />}
      <div className="wapp__bubble-group">
        <div className="wapp__bubble">{msg.text}</div>
        <div className="wapp__bubble-meta">
          <span className="wapp__bubble-timestamp">{msg.time}</span>
          
          {!isBot && (
            <span className={`wapp__bubble-receipt${msg.read ? " wapp__bubble-receipt--read" : ""}`}>
              {msg.read ? "✓✓" : "✓"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function WappTypingIndicator() {
  return (
    <div className="wapp__typing-row">
      <WappBotAvatar />
      <div className="wapp__typing-bubble" aria-label="Bot is typing">
        <span className="wapp__typing-dot" />
        <span className="wapp__typing-dot" />
        <span className="wapp__typing-dot" />
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ChatApp({
  botName = "CodeMatrix AI Assistant",
  userAvatarSrc = null,
  closeChat,
}) {
  // If token exists skip the pre-chat form, otherwise show it
  const hasToken = !!localStorage.getItem("token");
  const [userInfo, setUserInfo] = useState(hasToken ? {} : null);

  const greeting = {
    id: 1,
    role: "bot",
    text: "Hi there! 👋 How can I help you today?",
    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    read: false,
  };
  const [messages, setMessages] = useState(hasToken ? [greeting] : []);
  const [inputValue, setInputValue]   = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [errorMsg, setErrorMsg]       = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);

  // Kick off greeting once form is submitted
  const handleFormSubmit = (info) => {
    setUserInfo(info);
    setMessages([
      {
        id: 1,
        role: "bot",
        text: `Hi there! 👋 How can I help you today?`,
        time: formatTime(new Date()),
        read: false,
      },
    ]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  const handleInput = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  const buildHistory = useCallback(
    () => messages.map((m) => ({ role: m.role === "bot" ? "assistant" : "user", content: m.text })),
    [messages]
  );

  const handleSend = useCallback(
    async (textOverride) => {
      const trimmed = (textOverride ?? inputValue).trim();
      if (!trimmed || isBotTyping) return;

      setInputValue("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      setErrorMsg(null);

      const userMsg = {
        id: Date.now(),
        role: "user",
        text: trimmed,
        time: formatTime(new Date()),
        read: false,
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsBotTyping(true);

      try {
        const history = buildHistory();
        const response = await sendMessageToAPI(trimmed, history);
        const replyText = response.reply;
        setMessages((prev) => [
          ...prev.slice(0, -1),
          { ...prev[prev.length - 1], read: true },
          { id: Date.now() + 1, role: "bot", text: replyText, time: formatTime(new Date()) },
        ]);
      } catch (err) {
        const message = err.response?.data?.message || err.message || "Something went wrong.";
        setErrorMsg(message);
        setMessages((prev) => [
          ...prev.slice(0, -1),
          { ...prev[prev.length - 1], read: false },
        ]);
      } finally {
        setIsBotTyping(false);
        textareaRef.current?.focus();
      }
    },
    [inputValue, isBotTyping, buildHistory]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  
  const { speaking, speak, stop } = useTTS();
  // Stop TTS when chat closes

  const handleClose = useCallback(() => { stop(); closeChat(); }, [stop, closeChat]);

  return (
    <div className="wapp__shell" role="main">

      {/* ── HEADER ── */}
      <header className="wapp__header">
        <div className="wapp__header-left">
          <div className="wapp__header-avatar" aria-hidden="true">🤖</div>
          <div className="wapp__header-info">
            <div className="wapp__header-botname">{botName}</div>
            <div className="wapp__header-status">
              <span className="wapp__header-status-dot" /> Online
            </div>
          </div>
        </div>
        <button onClick={handleClose} className="wapp__header-minimize-btn" aria-label="Minimize">−</button>
      </header>

      {/* ── PRE-CHAT FORM  or  CHAT ── */}
      {!userInfo ? (
        <PreChatForm botName={botName} onSubmit={handleFormSubmit} />
      ) : (
        <>
          <section className="wapp__message-list" aria-live="polite" aria-label="Conversation">
            {messages.map((msg, i) => {
              const next = messages[i + 1];
              const showAvatar = !next || next.role !== msg.role;
              return (
                 <WappMessage key={msg.id} msg={msg} showAvatar={showAvatar} userAvatarSrc={userAvatarSrc}
    onSpeak={speak} isSpeaking={speaking === msg.id} />
              );
            })}
            {isBotTyping && <WappTypingIndicator />}
            <div ref={messagesEndRef} />
          </section>

          {errorMsg && (
            <div className="wapp__error-banner" role="alert">
              <span>⚠️ {errorMsg}</span>
              <button className="wapp__error-dismiss-btn" onClick={() => setErrorMsg(null)} aria-label="Dismiss">×</button>
            </div>
          )}

          <div className="wapp__composer">
            <div className="wapp__composer-inner">
              <textarea
                ref={textareaRef}
                className="wapp__composer-textarea"
                placeholder="Type your message here..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                aria-label="Message input"
              />
              <button
                className="wapp__composer-send-btn"
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isBotTyping}
                aria-label="Send message"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
              
            </div>
          </div>
        </>
      )}
    </div>
  );
}